<template>
    <Loading v-if="loading" />

    <div class="flex items-start justify-between gap-3 mb-4">
        <div class="min-w-0">
            <Title>Bands</Title>
        </div>
        <Button v-if="!hideAddButton" primary size="sm" @click="handleAdd">
            Add
        </Button>
    </div>

    <template v-if="groups.length">
        <section v-for="group in groups"
                 :key="group.brand"
                 class="bg-neutral-100 dark:bg-neutral-800 rounded my-4 overflow-hidden">
            <div class="bg-neutral-300 dark:bg-neutral-900 p-3 flex items-center justify-between">
                <div class="font-semibold">{{ group.brand }}</div>
                <div class="text-xs text-neutral-500 dark:text-neutral-300">{{ group.bands.length }} bands</div>
            </div>
            <div>
                <template v-for="bandSet in group.bandSets" :key="bandSet.name || 'no-band-set'">
                    <div v-if="bandSet.name"
                         class="border-t border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900/50 px-3 py-2 text-xs font-semibold text-neutral-500 dark:text-neutral-400">
                        {{ bandSet.name }}
                    </div>
                    <component v-for="band in bandSet.bands"
                               :is="renderAs"
                               :key="band.id"
                               :resource="band"/>
                </template>
            </div>
        </section>
    </template>

    <div v-else-if="!loading" class="bg-neutral-100 dark:bg-neutral-800 rounded p-3 text-sm text-neutral-500 dark:text-neutral-300">
        No bands yet.
    </div>

    <Paginator :paginator="paginator"
               :perPage="perPage"
               :perPageOptions="perPageOptions"
               @previous="handlePagePrevious"
               @next="handlePageNext"
               @update:perPage="handlePerPageChange"
    />
</template>

<script>
import {Band} from '@/internal';
import BandResourceListItem from '@/components/Band/ResourceListItem.vue';
import Paginator from '@/components/Paginator.vue';
import {mapActions} from 'vuex';

export default {
  name: 'BandResourceList',
  components: {
    Paginator,
  },
  computed: {
    bands() {
      return this.$store.getters['band/collection'](this.ids);
    },
    groups() {
      const groups = Object.values(this.bands.reduce((groups, band) => {
        const brand = band.brand || 'Custom';

        groups[brand] = groups[brand] || {
          brand,
          bands: [],
        };

        groups[brand].bands.push(band);

        return groups;
      }, {})).sort((a, b) => a.brand.localeCompare(b.brand));

      return groups.map((group) => {
        const hasBandSets = group.bands.some(band => band.bandSet);
        const bandSets = Object.values(group.bands.reduce((sets, band) => {
          const name = band.bandSet || (hasBandSets ? 'No Band Set' : '');

          sets[name] = sets[name] || {
            name,
            bands: [],
          };

          sets[name].bands.push(band);

          return sets;
        }, {})).sort((a, b) => {
          if (!a.name) return 1;
          if (!b.name) return -1;
          if (a.name === 'No Band Set') return 1;
          if (b.name === 'No Band Set') return -1;
          return a.name.localeCompare(b.name);
        });

        return {
          ...group,
          bandSets,
        };
      });
    },
    perPage() {
      return this.parsePerPage(this.$route.query.perPage);
    },
  },
  created() {
    this.loadBands();
  },
  data() {
    return {
      ids: [],
      loading: false,
      params: {
        include: [],
      },
      paginator: null,
      perPageOptions: [10, 25, 50, 100],
      resource: Band,
    };
  },
  methods: {
    ...mapActions({
      bandIndex: 'band/index',
    }),
    handleAdd() {
      this.$router.push(this.addRoute);
    },
    handlePageNext() {
      this.$router.push({
        name: this.$route.name,
        query: {
          ...this.$route.query,
          page: this.paginator.currentPage + 1,
          perPage: this.perPage,
        }
      });
    },
    handlePagePrevious() {
      this.$router.push({
        name: this.$route.name,
        query: {
          ...this.$route.query,
          page: this.paginator.currentPage - 1,
          perPage: this.perPage,
        }
      });
    },
    handlePerPageChange(perPage) {
      this.$router.push({
        name: this.$route.name,
        query: {
          ...this.$route.query,
          page: 1,
          perPage: this.parsePerPage(perPage),
        }
      });
    },
    loadBands() {
      this.loading = true;

      return this.bandIndex({
        params: {
          page: this.$route.query.page || 1,
          perPage: this.perPage,
          ...this.params,
          ...this.query,
        },
        replaceStore: true,
      }).then(({ids, paginator}) => {
        this.ids = ids;
        this.paginator = paginator;
      }).finally(() => {
        this.loading = false;
      });
    },
    parsePerPage(value) {
      const perPage = Number(value);

      if (this.perPageOptions.includes(perPage)) {
        return perPage;
      }

      return this.perPageOptions[0];
    },
  },
  props: {
    addRoute: {
      default() {
        return window.app.findRouteByName('band/create');
      },
      required: false,
    },
    hideAddButton: {
      default: false,
      type: Boolean,
    },
    query: {
      default: () => {
      },
      required: false,
    },
    renderAs: {
      default: () => {
        return BandResourceListItem;
      },
      required: false,
    },
  },
  watch: {
    ['$route']: {
      deep: true,
      handler() {
        this.loadBands();
      },
    }
  },
};
</script>

<style scoped lang="scss">

</style>
